import { useCallback, useEffect, useRef, useState } from 'react';
import '@excalidraw/excalidraw/index.css';
import type { ExcalidrawData, Theme } from '../../store/types.ts';

type ExcalidrawEditorProps = {
  theme: Theme;
  initialData?: ExcalidrawData;
  onChange: (data: ExcalidrawData) => void;
};

// Lazy-load Excalidraw to avoid massive bundle on initial load
let ExcalidrawModule: typeof import('@excalidraw/excalidraw') | null = null;

async function loadExcalidraw() {
  if (!ExcalidrawModule) {
    ExcalidrawModule = await import('@excalidraw/excalidraw');
  }
  return ExcalidrawModule;
}

export function ExcalidrawEditor({ theme, initialData, onChange }: ExcalidrawEditorProps) {
  const [loaded, setLoaded] = useState(false);
  const [ExcalidrawComponent, setExcalidrawComponent] = useState<React.ComponentType<Record<string, unknown>> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadExcalidraw().then((mod) => {
      if (cancelled) return;
      setExcalidrawComponent(() => mod.Excalidraw);
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  const handleChange = useCallback(
    (elements: unknown[], appState: Record<string, unknown>) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onChange({
          elements: elements as ExcalidrawData['elements'],
          appState,
          files: {},
        });
      }, 500);
    },
    [onChange],
  );

  if (!loaded || !ExcalidrawComponent) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'var(--ds-text-tertiary)',
        fontSize: '13px',
      }}>
        Loading Excalidraw...
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ExcalidrawComponent
        theme={theme === 'dark' ? 'dark' : 'light'}
        zenModeEnabled={false}
        initialData={initialData ? {
          elements: initialData.elements,
          appState: {
            ...initialData.appState,
            zenModeEnabled: false,
            collaborators: new Map(),
          },
        } : {
          appState: {
            zenModeEnabled: false,
            collaborators: new Map(),
          },
        }}
        onChange={handleChange}
        UIOptions={{
          canvasActions: {
            toggleTheme: false,
          },
        }}
      />
    </div>
  );
}
